import React, { useState } from "react";
import { Controller, useForm } from "react-hook-form";
import cx from "classnames";
import stylesV2 from "./Form.module.scss";
import stylesV3 from "./FormV2.module.scss";
import { TextInput } from "components/TextInput";
import { Button } from "components/Button";
import { Typeahead } from "components/Typeahead";
import { SmsCheckbox } from "components/SmsCheckbox";
import morningSunnyBlackIcon from "images/morning-sunny-black.svg";
import morningSunnyBlueIcon from "images/morning-sunny-blue.svg";
import afternoonSunnyBlackIcon from "images/afternoon-sunny-black.svg";
import afternoonSunnyBlueIcon from "images/afternoon-sunny-blue.svg";
import nightLightRoundBlackIcon from "images/nightlight-round-black.svg";
import nightLightRoundBlueIcon from "images/nightlight-round-blue.svg";
import { useSchedulerTracking } from "hooks/useSchedulerTracking";
import { DesiredDayOfWeek } from "types/desiredDayOfWeek";
import { DesiredTimeOfDay } from "types/desiredTimeOfDay";
import { Office } from "types/office";
import { ExamType } from "types/examType";
import { PatientReturnStatus } from "types/patientReturnStatus";
import { MaskedPhoneInput } from "components/MaskedTextInput/MaskedTextInput";
import { isValidPhoneNumber } from "utils/phoneNumberValidation";
import { presenceNameValidation } from "utils/presenceNameValidation";
import { sanitizePatientNameInput } from "utils/sanitizePatientNameInput";
import {
  isEmail,
  isValidTLDLength,
  acceptableCharacters,
} from "utils/emailValidation";
import { areExtendedPatientNameFieldsEnabled } from "featureFlags/areExtendedPatientNameFieldsEnabled";
import { useSchedulingABVariant } from "../../utils/useSchedulingABVariant";

type FormProps = {
  onSubmit: (params: FormData) => void;
  isLoading: boolean;
  office: Office;
  selectedDoctorId?: string;
};

export interface FormData {
  firstName: string;
  lastName: string;
  phoneNumber: string;
  email: string;
  examType: ExamType;
  patientReturnStatus: PatientReturnStatus;
  desiredDayOfWeek: DesiredDayOfWeek;
  desiredTimeOfDay?: string;
  smsOptInNotices?: number;
  smsOptInPromos?: number;
}

export const waitlistFormAria = {
  describedby: "waitlist-form-description",
};

export const Form: React.FC<FormProps> = ({
  onSubmit,
  isLoading,
  office,
  selectedDoctorId = "",
}) => {
  const { isV3 } = useSchedulingABVariant();
  const styles = isV3 ? stylesV3 : stylesV2;
  const { trackSchedulerEvent } = useSchedulerTracking(selectedDoctorId);
  const nameMaxLength = areExtendedPatientNameFieldsEnabled() ? 30 : 20;

  const {
    register,
    formState: { errors },
    handleSubmit,
    control,
    clearErrors,
    setValue,
  } = useForm<FormData>({
    mode: "onSubmit",
  });

  const firstName = register(
    "firstName",
    presenceNameValidation(
      "Not a valid first name; no special characters except '-' allowed",
      nameMaxLength
    )
  );
  const lastName = register(
    "lastName",
    presenceNameValidation(
      "Not a valid last name; no special characters except '-' allowed",
      nameMaxLength
    )
  );
  const phoneNumber = register("phoneNumber", { validate: isValidPhoneNumber });

  const email = register("email", {
    required: "Not a valid email",
    validate: {
      isEmail: (value) => isEmail(value),
      isValidTLDLength: (value) => isValidTLDLength(value),
      acceptableCharacters: (value) => acceptableCharacters(value),
    },
  });

  const [selectedDesiredTimeOfDay, setSelectedDesiredTimeOfDay] = useState<
    Record<DesiredTimeOfDay, boolean>
  >({
    [DesiredTimeOfDay.Morning]: false,
    [DesiredTimeOfDay.Afternoon]: false,
    [DesiredTimeOfDay.Evening]: false,
  });
  const smsOptInNotices = register("smsOptInNotices");
  const smsOptInPromos = register("smsOptInPromos");
  const onSubmitForm = ({
    firstName,
    lastName,
    phoneNumber,
    email,
    examType,
    patientReturnStatus,
    desiredDayOfWeek,
    smsOptInNotices,
    smsOptInPromos,
  }: FormData) => {
    const desiredTimeOfDay = Object.keys(selectedDesiredTimeOfDay)
      .filter((a: DesiredTimeOfDay) => (selectedDesiredTimeOfDay[a] ? a : ""))
      .toString();

    onSubmit({
      firstName,
      lastName,
      phoneNumber,
      email,
      examType,
      patientReturnStatus,
      desiredDayOfWeek: parseInt(desiredDayOfWeek.toString()),
      desiredTimeOfDay,
      smsOptInNotices,
      smsOptInPromos,
    });
  };

  const toggleDesiredTimeOfDay = (desiredTimeOfDayValue: DesiredTimeOfDay) => {
    setSelectedDesiredTimeOfDay((prevState) => ({
      ...prevState,
      [desiredTimeOfDayValue]: !prevState[desiredTimeOfDayValue],
    }));
  };

  return (
    <div className={styles.form}>
      <form onSubmit={handleSubmit(onSubmitForm)} aria-label="Waitlist form">
        <div className={cx(styles.headerContent, styles.prompt)}>
          Hey! before you go...
        </div>
        <p id={waitlistFormAria.describedby} className={styles.describedBy}>
          <b>Sign-up for our waitlist.</b>
          <br />
          We&apos;ll let you know if a convenient time is available for your eye
          exam at <b>{office.displayName}.</b>
        </p>
        <div className={styles.desktopTwoColumns}>
          <TextInput
            autoFocus
            {...email}
            inputType="email"
            onChange={(e) => {
              email.onChange(e);
            }}
            errorClassName={cx({
              [styles.emptyError]: !errors.email?.message,
            })}
            errorMessage={errors.email?.message}
          >
            Preferred Email Address
          </TextInput>
          <MaskedPhoneInput
            {...phoneNumber}
            onChange={(e) => {
              clearErrors();
              phoneNumber.onChange(e);
            }}
            errorClassName={cx({
              [styles.emptyError]: !errors.phoneNumber?.message,
            })}
            errorMessage={errors.phoneNumber?.message}
            setValue={setValue}
          >
            Phone Number
          </MaskedPhoneInput>
        </div>
        <div className={styles.desktopTwoColumns}>
          <TextInput
            {...firstName}
            maxLength={nameMaxLength}
            inputType="text"
            placeholder=""
            onChange={(e) => {
              clearErrors();
              e.target.value = sanitizePatientNameInput(
                e.target.value,
                nameMaxLength
              );
              firstName.onChange(e);
            }}
            errorClassName={cx({
              [styles.emptyError]: !errors.firstName?.message,
            })}
            errorMessage={errors.firstName?.message}
          >
            First Name
          </TextInput>
          <TextInput
            {...lastName}
            maxLength={nameMaxLength}
            inputType="text"
            onChange={(e) => {
              clearErrors();
              e.target.value = sanitizePatientNameInput(
                e.target.value,
                nameMaxLength
              );
              lastName.onChange(e);
            }}
            errorClassName={cx({
              [styles.emptyError]: !errors.lastName?.message,
            })}
            errorMessage={errors.lastName?.message}
          >
            Last Name
          </TextInput>
        </div>
        <Controller
          rules={{ required: "Please select option" }}
          render={({ field, fieldState }) => (
            <Typeahead
              field={field}
              name="examType"
              label="Tell us about yourself!"
              placeholder="Select"
              creatable={false}
              clearable={false}
              options={[
                {
                  label: "I only wear glasses",
                  value: ExamType.GlassesTryContacts.toString(),
                },
                {
                  label: "I only wear contacts",
                  value: ExamType.Contacts.toString(),
                },
                {
                  label: "I wear glasses and contacts",
                  value: ExamType.GlassesAndContacts.toString(),
                },
                {
                  label: "I do not wear glasses or contacts",
                  value: ExamType.NoGlassesOrContacts.toString(),
                },
              ]}
              errorMessage={fieldState.error?.message}
            />
          )}
          name="examType"
          control={control}
        />

        <Controller
          rules={{ required: "Please select option" }}
          render={({ field, fieldState }) => (
            <Typeahead
              field={field}
              name="patientReturnStatus"
              label="Have you previously visited a MyEyeDr. location before?"
              placeholder="Select"
              creatable={false}
              clearable={false}
              options={[
                {
                  label: "Yes",
                  value: PatientReturnStatus.Returning.toString(),
                },
                {
                  label: "No",
                  value: PatientReturnStatus.New.toString(),
                },
                {
                  label: "Not Sure",
                  value: PatientReturnStatus.Unknown.toString(),
                },
              ]}
              errorMessage={fieldState.error?.message}
            />
          )}
          name="patientReturnStatus"
          control={control}
        />
        <Controller
          rules={{ required: "Please select option" }}
          render={({ field, fieldState }) => (
            <Typeahead
              field={field}
              name="desiredDayOfWeek"
              label="Desired Day of Appointment"
              placeholder="Select"
              creatable={false}
              clearable={false}
              options={[
                {
                  label: "Earliest Available",
                  value: DesiredDayOfWeek.Earliest.toString(),
                },
                { label: "Monday", value: DesiredDayOfWeek.Monday.toString() },
                {
                  label: "Tuesday",
                  value: DesiredDayOfWeek.Tuesday.toString(),
                },
                {
                  label: "Wednesday",
                  value: DesiredDayOfWeek.Wednesday.toString(),
                },
                {
                  label: "Thursday",
                  value: DesiredDayOfWeek.Thursday.toString(),
                },
                { label: "Friday", value: DesiredDayOfWeek.Friday.toString() },
                {
                  label: "Saturday",
                  value: DesiredDayOfWeek.Saturday.toString(),
                },
                { label: "Sunday", value: DesiredDayOfWeek.Sunday.toString() },
              ]}
              errorMessage={fieldState.error?.message}
            />
          )}
          name="desiredDayOfWeek"
          control={control}
        />

        <div className={styles.desiredTimeOfDayRow}>
          <div className={styles.desiredTimeOfDay}>Desired Time Of Day</div>
          <div className={styles.circleRow}>
            <div>
              <button
                data-testid="morning-waitlist"
                type="button"
                className={`${styles.circleButton} ${
                  selectedDesiredTimeOfDay[DesiredTimeOfDay.Morning]
                    ? styles.selectedCircle
                    : styles.unSelectedCircle
                }`}
                onClick={() => {
                  trackSchedulerEvent("morning-waitlist", {
                    doctorId: selectedDoctorId,
                  });
                  toggleDesiredTimeOfDay(DesiredTimeOfDay.Morning);
                }}
              >
                <img
                  src={
                    !selectedDesiredTimeOfDay[DesiredTimeOfDay.Morning]
                      ? morningSunnyBlackIcon
                      : morningSunnyBlueIcon
                  }
                  width="24px"
                  height="24px"
                  alt="morning_img"
                />
              </button>
              <div className={styles.desiredTimeOfDayHeading}>Morning</div>
              <div className={styles.desiredTimeOfDaySubHeading}>
                before noon
              </div>
            </div>
            <div>
              <button
                data-testid="afternoon-waitlist"
                type="button"
                className={`${styles.circleButton} ${
                  selectedDesiredTimeOfDay[DesiredTimeOfDay.Afternoon]
                    ? styles.selectedCircle
                    : styles.unSelectedCircle
                }`}
                onClick={() => {
                  trackSchedulerEvent("afternoon-waitlist", {
                    doctorId: selectedDoctorId,
                  });
                  toggleDesiredTimeOfDay(DesiredTimeOfDay.Afternoon);
                }}
              >
                <img
                  src={
                    !selectedDesiredTimeOfDay[DesiredTimeOfDay.Afternoon]
                      ? afternoonSunnyBlackIcon
                      : afternoonSunnyBlueIcon
                  }
                  width="24px"
                  height="24px"
                  alt="afternoon_img"
                />
              </button>

              <div className={styles.desiredTimeOfDayHeading}>Afternoon</div>
              <div className={styles.desiredTimeOfDaySubHeading}>
                noon to 5pm
              </div>
            </div>
            <div>
              <button
                data-testid="evening-waitlist"
                type="button"
                className={`${styles.circleButton} ${
                  selectedDesiredTimeOfDay[DesiredTimeOfDay.Evening]
                    ? styles.selectedCircle
                    : styles.unSelectedCircle
                }`}
                onClick={() => {
                  trackSchedulerEvent("evening-waitlist", {
                    doctorId: selectedDoctorId,
                  });
                  toggleDesiredTimeOfDay(DesiredTimeOfDay.Evening);
                }}
              >
                <img
                  src={
                    !selectedDesiredTimeOfDay[DesiredTimeOfDay.Evening]
                      ? nightLightRoundBlackIcon
                      : nightLightRoundBlueIcon
                  }
                  width="24px"
                  height="24px"
                  alt="evening_img"
                />
              </button>
              <div className={styles.desiredTimeOfDayHeading}>Evening</div>
              <div className={styles.desiredTimeOfDaySubHeading}>after 5pm</div>
            </div>
          </div>
        </div>
        <div>
          <SmsCheckbox
            label="I'd like to receive text reminders about my appointment, about my prescription, and when my glasses or contacts are ready. (MyEyeDr. Notices) *"
            {...smsOptInNotices}
            defaultChecked={true}
            onChange={(e) => {
              clearErrors();
              smsOptInNotices.onChange(e);
            }}
          />
          <div style={{ marginTop: 10 }}>
            <SmsCheckbox
              label="I'd like to receive promotional texts with eyewear and eyecare offers. (MyEyeDr. Promos) *"
              {...smsOptInPromos}
              defaultChecked={true}
              onChange={(e) => {
                clearErrors();
                smsOptInPromos.onChange(e);
              }}
            />
          </div>
          <div className={styles.buttonContainer}>
            <Button
              onClick={handleSubmit(onSubmitForm)}
              type="submit"
              size="large"
              text="Submit"
              className={styles.button}
              loading={isLoading}
              disabled={isLoading}
            />
          </div>
          <p className={styles.smsHelp}>
            * When scheduling an appointment on MyEyeDr.’s website, you are subscribed to receive email messages regarding your appointment and treatment as well as discounts and promotions. You can opt out of email messages at any time through the <i>Manage Email Preferences</i> link in any email or by contacting us at {" "}
            <a href="mailto:PatientRelations@myeyedr.com">
              PatientRelations@myeyedr.com
            </a>.
          </p>
          <p className={styles.smsHelp}>
            * Message frequency varies. Message and data rates may apply. At any
            time you can text HELP for help or text STOP to stop notifications.{" "}
            <a
              href="https://www.myeyedr.com/sms-terms-service"
              target="_blank"
              rel="noreferrer"
            >
              SMS Terms of Service
            </a>{" "}
            |{" "}
            <a
              href="https://www.myeyedr.com/privacy-policy-practices"
              target="_blank"
              rel="noreferrer"
            >
              MyEyeDr. Privacy Policy
            </a>
          </p>
        </div>
        <div className={styles.agreeText}>
          * I agree that you can contact me by text at the mobile number
          provided. By agreeing I consent to receiving texts about promotional
          offers related to eyewear and eyecare offers by any type of dialing
          equipment, automated technology, or other messaging system to send the
          communications. I understand message frequency varies and that data
          and message rates may apply.{" "}
          <u>
            I understand that I am not required to agree to such messaging, and
            it is not a condition for the purchase of any goods or services.
          </u>
          <p>* For more information about offers, discounts, and promotions - including Terms & Conditions - visit our{" "} 
            <a href="https://www.myeyedr.com/offers" target="blank">Offers page.</a>
          </p>
        </div>
      </form>
    </div>
  );
};
